/**
 * ML Metrics API Client - PRD-3619
 * Frontend API client for Performance Learning dashboard
 */

import { apiClient } from './apiClient';

export interface ModelMetrics {
  model_version: string;
  training_date: string;
  samples_count: number;
  accuracy: number | null;
  auc_score: number | null;
  mse: number | null;
  feature_importance: Record<string, number>;
}

export interface PatternInsight {
  pattern_type: 'keyword' | 'emotion' | 'length' | 'punctuation';
  pattern_value: string;
  avg_ctr: number;
  lift_vs_baseline: number;
  confidence: number;
  sample_size: number;
}

export interface CTRTrend {
  date: string;
  baseline_ctr: number;
  ml_ctr: number;
  improvement_pct: number;
}

export interface MLDashboardData {
  approval_model: ModelMetrics;
  quality_model: ModelMetrics;
  total_samples: number;
  samples_last_7_days: number;
  samples_last_30_days: number;
  top_patterns: PatternInsight[];
  ctr_improvement_30d: number;
  ctr_trend: CTRTrend[];
  feature_importance: Record<string, number>;
  learning_status: 'active' | 'needs_data' | 'training';
  last_training: string;
  next_training: string;
}

export interface CopySuggestion {
  type: string;
  suggestion: string;
  impact: string;
}

export interface CopyAnalysis {
  copy_text: string;
  quality_score: number;
  approval_probability: number;
  suggestions: CopySuggestion[];
}

// Mock data for local development
const MOCK_DASHBOARD_DATA: MLDashboardData = {
  approval_model: {
    model_version: 'v20260209_091000',
    training_date: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    samples_count: 1247,
    accuracy: 0.87,
    auc_score: 0.91,
    mse: null,
    feature_importance: {
      copy_length: 0.18,
      word_count: 0.12,
      avg_word_length: 0.08,
      has_emoji: 0.15,
      has_numbers: 0.09,
      has_question: 0.14,
      has_exclamation: 0.11,
      ctr_history: 0.07,
      cvr_history: 0.04,
      engagement: 0.02,
    },
  },
  quality_model: {
    model_version: 'v20260209_091000',
    training_date: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    samples_count: 1247,
    accuracy: null,
    auc_score: null,
    mse: 0.0234,
    feature_importance: {
      copy_length: 0.18,
      word_count: 0.12,
      avg_word_length: 0.08,
      has_emoji: 0.15,
      has_numbers: 0.09,
      has_question: 0.14,
      has_exclamation: 0.11,
      ctr_history: 0.07,
      cvr_history: 0.04,
      engagement: 0.02,
    },
  },
  total_samples: 1247,
  samples_last_7_days: 187,
  samples_last_30_days: 589,
  top_patterns: [
    { pattern_type: 'keyword', pattern_value: 'limited time', avg_ctr: 0.038, lift_vs_baseline: 52.0, confidence: 0.92, sample_size: 156 },
    { pattern_type: 'keyword', pattern_value: 'exclusive', avg_ctr: 0.035, lift_vs_baseline: 40.0, confidence: 0.88, sample_size: 203 },
    { pattern_type: 'emotion', pattern_value: 'urgency', avg_ctr: 0.034, lift_vs_baseline: 36.0, confidence: 0.91, sample_size: 312 },
    { pattern_type: 'length', pattern_value: '50-80 chars', avg_ctr: 0.032, lift_vs_baseline: 28.0, confidence: 0.95, sample_size: 847 },
    { pattern_type: 'punctuation', pattern_value: 'ends_with_question', avg_ctr: 0.031, lift_vs_baseline: 24.0, confidence: 0.86, sample_size: 189 },
  ],
  ctr_improvement_30d: 12.4,
  ctr_trend: Array.from({ length: 30 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    const improvement = Math.min(0.15, i * 0.005);
    return {
      date: date.toISOString().split('T')[0],
      baseline_ctr: 0.025 + (Math.random() - 0.5) * 0.004,
      ml_ctr: 0.025 * (1 + improvement) + (Math.random() - 0.5) * 0.002,
      improvement_pct: improvement * 100,
    };
  }),
  feature_importance: {
    copy_length: 0.18,
    word_count: 0.12,
    avg_word_length: 0.08,
    has_emoji: 0.15,
    has_numbers: 0.09,
    has_question: 0.14,
    has_exclamation: 0.11,
    ctr_history: 0.07,
    cvr_history: 0.04,
    engagement: 0.02,
  },
  learning_status: 'active',
  last_training: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
  next_training: new Date(Date.now() + 18 * 60 * 60 * 1000).toISOString(),
};

const USE_MOCK = true; // Set to false when backend is ready

/**
 * Get ML dashboard data with model metrics, patterns, and CTR trends
 */
export async function getMLDashboard(): Promise<MLDashboardData> {
  if (USE_MOCK) {
    return MOCK_DASHBOARD_DATA;
  }
  
  try {
    const response = await apiClient.get('/api/v1/ml-metrics/dashboard');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch ML dashboard:', error);
    return MOCK_DASHBOARD_DATA;
  }
}

/**
 * Get discovered patterns filtered by type and confidence
 */
export async function getPatterns(
  patternType?: string,
  minConfidence: number = 0.8
): Promise<PatternInsight[]> {
  if (USE_MOCK) {
    let patterns = MOCK_DASHBOARD_DATA.top_patterns;
    if (patternType) {
      patterns = patterns.filter(p => p.pattern_type === patternType);
    }
    return patterns.filter(p => p.confidence >= minConfidence);
  }
  
  try {
    const params = new URLSearchParams();
    if (patternType) params.append('pattern_type', patternType);
    params.append('min_confidence', minConfidence.toString());
    
    const response = await apiClient.get(`/api/v1/ml-metrics/patterns?${params}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch patterns:', error);
    return MOCK_DASHBOARD_DATA.top_patterns;
  }
}

/**
 * Trigger manual model retraining
 */
export async function triggerTraining(): Promise<{ status: string; message: string }> {
  if (USE_MOCK) {
    return {
      status: 'training_started',
      message: 'Model training initiated. Check dashboard in ~5 minutes.',
    };
  }
  
  const response = await apiClient.post('/api/v1/ml-metrics/trigger-training');
  return response.data;
}

/**
 * Get ML-powered suggestions for copy improvement
 */
export async function getCopySuggestions(copyText: string): Promise<CopyAnalysis> {
  if (USE_MOCK) {
    const suggestions: CopySuggestion[] = [];
    
    if (copyText.length < 50) {
      suggestions.push({
        type: 'length',
        suggestion: 'Consider expanding copy to 50-80 characters for optimal CTR',
        impact: '+28% CTR lift',
      });
    }
    
    const lower = copyText.toLowerCase();
    if (!lower.includes('limited') && !lower.includes('exclusive')) {
      suggestions.push({
        type: 'keyword',
        suggestion: "Consider adding urgency keywords like 'limited time' or 'exclusive'",
        impact: '+40-52% CTR lift',
      });
    }
    
    if (!copyText.includes('!') && !copyText.includes('?')) {
      suggestions.push({
        type: 'punctuation',
        suggestion: 'Adding a question or exclamation can increase engagement',
        impact: '+24% CTR lift',
      });
    }
    
    return {
      copy_text: copyText,
      quality_score: 0.65 + Math.random() * 0.2,
      approval_probability: 0.75 + Math.random() * 0.2,
      suggestions,
    };
  }
  
  const response = await apiClient.get(`/api/v1/ml-metrics/suggestions/${encodeURIComponent(copyText)}`);
  return response.data;
}
